import { useState, useMemo } from "react";
import { 
  AlertTriangle, 
  Check, 
  RefreshCcw, 
  XCircle, 
  Ticket,
  ArrowRight,
  Clock
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { CommandEntry } from "@/hooks/useCommandEntries";
import { EntryType } from "@/hooks/useEntryTypes";
import { useOperationLogs } from "@/hooks/useOperationLogs";

interface ManageEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: CommandEntry | null;
  entryTypes: EntryType[];
  onCancel: (entryId: string, reason?: string) => Promise<{ success: boolean; entry?: CommandEntry }>;
  onUpdate: (
    entryId: string,
    newEntryType: {
      id: string;
      name: string;
      amount: number;
      entry_mode: "fixed" | "minimum_consumption";
    },
    reason?: string
  ) => Promise<{ success: boolean; difference?: number }>;
}

export function ManageEntryDialog({
  open,
  onOpenChange,
  entry,
  entryTypes,
  onCancel,
  onUpdate,
}: ManageEntryDialogProps) {
  const { logOperation } = useOperationLogs();
  const [selectedEntryType, setSelectedEntryType] = useState<EntryType | null>(null);
  const [reason, setReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"alter" | "cancel">("alter");

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const difference = useMemo(() => {
    if (!entry || !selectedEntryType) return 0;
    return selectedEntryType.amount - entry.amount;
  }, [entry, selectedEntryType]);

  const activeEntryTypes = entryTypes.filter(
    (et) => et.is_active && et.id !== entry?.entry_type_id
  );

  const handleUpdate = async () => {
    if (!entry || !selectedEntryType) return;

    setIsSubmitting(true);
    const result = await onUpdate(
      entry.id,
      {
        id: selectedEntryType.id,
        name: selectedEntryType.name,
        amount: selectedEntryType.amount,
        entry_mode: selectedEntryType.entry_mode,
      },
      reason
    );

    if (result.success) {
      await logOperation("update", "command_entry", entry.id, {
        command_number: entry.command_number,
        old_type: entry.entry_type_name,
        old_amount: entry.amount,
        new_type: selectedEntryType.name,
        new_amount: selectedEntryType.amount,
        difference: result.difference,
        reason,
      });
      resetAndClose();
    }
    setIsSubmitting(false);
  };

  const handleCancel = async () => {
    if (!entry) return;

    setIsSubmitting(true);
    const result = await onCancel(entry.id, cancelReason);

    if (result.success) {
      await logOperation("cancel", "command_entry", entry.id, {
        command_number: entry.command_number,
        entry_type: entry.entry_type_name,
        amount: entry.amount,
        reason: cancelReason,
      });
      resetAndClose();
    }
    setIsSubmitting(false);
  };

  const resetAndClose = () => {
    setSelectedEntryType(null);
    setReason("");
    setCancelReason("");
    setActiveTab("alter");
    onOpenChange(false);
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Gerenciar Entrada
          </DialogTitle>
          <DialogDescription>
            Comanda {entry.command_number.padStart(3, "0")}
          </DialogDescription>
        </DialogHeader>

        {/* Entry Info */}
        <div className="bg-muted rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Tipo atual</span>
            <div className="flex items-center gap-2">
              <span className="font-medium">{entry.entry_type_name}</span>
              <Badge variant="secondary" className="text-xs">
                {entry.entry_mode === "fixed" ? "Fixa" : "Consumação Mínima"}
              </Badge>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Valor</span>
            <span className="font-bold text-lg">{formatCurrency(entry.amount)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Aberta em</span>
            <span className="text-sm flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {entry.created_at ? formatDateTime(entry.created_at) : "-"}
            </span>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === "alter" ? "default" : "outline"}
            className="flex-1"
            onClick={() => setActiveTab("alter")}
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Alterar Tipo
          </Button>
          <Button
            variant={activeTab === "cancel" ? "destructive" : "outline"}
            className="flex-1"
            onClick={() => setActiveTab("cancel")}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
        </div>

        <Separator />

        {/* Alter Tab */}
        {activeTab === "alter" && (
          <div className="space-y-4">
            <Label>Selecione o novo tipo de entrada</Label>
            
            {activeEntryTypes.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                Nenhum outro tipo de entrada disponível
              </div>
            ) : (
              <div className="grid gap-2 max-h-48 overflow-y-auto">
                {activeEntryTypes.map((et) => (
                  <button
                    key={et.id}
                    onClick={() => setSelectedEntryType(et)}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border transition-all text-left",
                      selectedEntryType?.id === et.id
                        ? "bg-primary/5 border-primary ring-2 ring-primary"
                        : "bg-background hover:bg-muted border-border"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {selectedEntryType?.id === et.id ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <Ticket className="h-4 w-4" />
                      )}
                      <span className="font-medium">{et.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {et.entry_mode === "fixed" ? "Fixa" : "Cons. Mín."}
                      </Badge>
                    </div>
                    <span className="font-bold">{formatCurrency(et.amount)}</span>
                  </button>
                ))}
              </div>
            )}

            {selectedEntryType && (
              <div className="bg-muted rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <span>{entry.entry_type_name}</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="font-medium">{selectedEntryType.name}</span>
                </div>
                <div className={cn(
                  "text-center font-bold text-lg",
                  difference > 0 ? "text-orange-600" : difference < 0 ? "text-green-600" : "text-muted-foreground"
                )}>
                  {difference > 0 && `Cliente paga +${formatCurrency(difference)}`}
                  {difference < 0 && `Devolver ${formatCurrency(Math.abs(difference))} ao cliente`}
                  {difference === 0 && "Sem diferença de valor"}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="reason">Motivo (opcional)</Label>
              <Textarea
                id="reason"
                placeholder="Ex: Cliente pediu upgrade para VIP"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <Button
              className="w-full"
              disabled={!selectedEntryType || isSubmitting}
              onClick={handleUpdate}
            >
              {isSubmitting ? "Alterando..." : "Confirmar Alteração"}
            </Button>
          </div>
        )}

        {/* Cancel Tab */}
        {activeTab === "cancel" && (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Esta ação irá:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Estornar o valor de {formatCurrency(entry.amount)}</li>
                  <li>Liberar a comanda {entry.command_number.padStart(3, "0")} para nova entrada</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="cancelReason">Motivo do cancelamento</Label>
              <Textarea
                id="cancelReason"
                placeholder="Ex: Comanda aberta por engano"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>

            <Button
              variant="destructive"
              className="w-full"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              {isSubmitting ? "Cancelando..." : "Cancelar Entrada com Estorno"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
